/** Readiness rollup chip for the workflow gallery card footer (Phase 4
 * slice 1, Task 11). Reuses `useWorkflowReadiness` (Task 9) — no
 * duplicate query. Renders nothing while loading or on error (silent
 * degrade, no spinner).
 *
 * Non-interactive by design (fix-round-1 ruling): this chip renders
 * inside `WorkflowGalleryCard`'s `footer` slot, and on the gallery page
 * (`WorkflowsPage.tsx`) that whole card is already wrapped in its own
 * `<Link to={`/workflows/${id}`}>`. An interactive element here (an
 * anchor/link) would nest inside that outer anchor — invalid per the
 * HTML5 content model (interactive content inside interactive content)
 * and contrary to `WorkflowGalleryCard.tsx`'s own doc comment ("no
 * nested links/buttons"). The rollup is plain visual information: the
 * user reaches the Readiness tab via the card's own navigation (into
 * Overview) plus the Readiness tab wired in Task 10 — a `title`
 * attribute carries the same information scent on hover without adding
 * a second interactive target.
 * Spec: docs/superpowers/specs/2026-08-17-workflow-readiness-checklist-design.md */
import { Badge } from '@/components/ui'
import { useWorkflowReadiness } from '@/pages/WorkflowOverview/ReadinessTab'
import type { WorkflowReadinessRead } from '@/types/workflowConfig'

// Mirrors ReadinessTab.tsx's STATUS_BADGE variant/label mapping (same
// semantic colors), swapping the item-level 'ok' for the rollup's 'ready'.
const ROLLUP_BADGE: Record<
  WorkflowReadinessRead['rollup'],
  { variant: 'success' | 'warning' | 'secondary' | 'destructive'; label: string }
> = {
  ready: { variant: 'success', label: 'Ready' },
  attention: { variant: 'warning', label: 'Attention' },
  unknown: { variant: 'secondary', label: 'Unknown' },
  blocked: { variant: 'destructive', label: 'Blocked' },
}

export function ReadinessChip({ workflowId }: { workflowId: string }) {
  const readiness = useWorkflowReadiness(workflowId)
  if (!readiness.data) return null // loading/error: chip stays silent (no spinner)

  const { variant, label } = ROLLUP_BADGE[readiness.data.rollup] ?? ROLLUP_BADGE.unknown

  return (
    <Badge variant={variant} title={`Readiness: ${label}`}>
      {label}
    </Badge>
  )
}
